import React from 'react';
import { Input, Select, Segmented, Button } from 'antd';
import { Search, CheckCircle2, XCircle, Clock3, MinusCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';

/**
 * ============================================================================
 *  AdminUI — small, reusable AntD-based presentational pieces for the admin
 *  panel, styled to the minimal near-monochrome design system.
 *
 *  Real AntD components (Input, Select, Segmented, Button) inherit the teal
 *  ConfigProvider theme from <AdminThemeProvider>; the TG-prefixed classes
 *  (.tg-kpis, .tg-st, .tg-org) are styled in adminDesignSystem.js.
 *
 *  These are visual-only — parents own all state and handlers.
 * ============================================================================
 */

/** Two initials from a name, for the org avatar. */
export const initials = (name = '') =>
  name
    .replace(/[^A-Za-z ]/g, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '—';

/** Org identity cell: avatar + name + email. */
export const OrgCell = ({ name, email }) => (
  <div className="tg-org">
    <div className="tg-org-ava">{initials(name)}</div>
    <div>
      <b>{name}</b>
      {email ? <span>{email}</span> : null}
    </div>
  </div>
);

/**
 * Column header with a small pastel icon tile (Unflip-style).
 * `tone` 1-5 picks the tile color; `icon` is a lucide component.
 */
export const ColHeader = ({ icon: Icon, tone = 1, children }) => (
  <span className="tg-th">
    {Icon ? (
      <span className={`tg-th-ico i${tone}`}>
        <Icon size={14} strokeWidth={2} />
      </span>
    ) : null}
    {children}
  </span>
);

/**
 * Status: icon + colored text (Unflip-style).
 * tone ∈ 'ok' | 'info' | 'warn' | 'bad' | 'off'
 */
const TONE_ICON = {
  ok: CheckCircle2,
  info: Clock3,
  warn: Clock3,
  bad: XCircle,
  off: MinusCircle,
};
export const StatusBadge = ({ tone = 'off', children }) => {
  const Icon = TONE_ICON[tone] || MinusCircle;
  return (
    <span className={`tg-st ${tone}`}>
      <Icon size={17} strokeWidth={2} />
      {children}
    </span>
  );
};

/** Map a raw subscription status → tone + label. */
export const statusTone = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'active': return { tone: 'ok', label: 'Active' };
    case 'trialing': case 'trial': return { tone: 'info', label: 'Trialing' };
    case 'past_due': case 'past due': return { tone: 'bad', label: 'Past Due' };
    case 'canceled': case 'cancelled': case 'churned': return { tone: 'off', label: 'Churned' };
    default: return { tone: 'off', label: status || '—' };
  }
};

/**
 * Pastel KPI cards (Unflip-style). `items`:
 *   [{ label, value, unit?, sub?, delta?: {dir:'up'|'dn', text} }]
 * Cards rotate through blue / purple / orange / teal pastels with big colored
 * numbers and a trend-arrow chip. Animation + hover-lift live in the stylesheet.
 */
const KPI_COLORS = ['c-blue', 'c-purple', 'c-orange', 'c-teal'];
export const KpiStrip = ({ items = [] }) => (
  <div className="tg-kpis">
    {items.map((it, i) => {
      const dir = it.delta?.dir === 'dn' ? 'dn' : 'up';
      const Arrow = dir === 'dn' ? ArrowDownRight : ArrowUpRight;
      return (
        <div className={`tg-kpi ${KPI_COLORS[i % KPI_COLORS.length]}`} key={i}>
          <div className="l">{it.label}</div>
          <div className="row">
            <div className="v">
              {it.value}
              {it.unit ? <small>{it.unit}</small> : null}
            </div>
            {(it.sub || it.delta) && (
              <>
                <div className="div" />
                <div className="imp">
                  {it.delta ? it.delta.text : it.sub}
                  <span className="arw"><Arrow size={13} strokeWidth={2.5} /></span>
                </div>
              </>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

/**
 * Minimal toolbar: AntD Input.Search + Select filters + optional Segmented.
 * All props are controlled by the parent.
 */
export const AdminToolbar = ({
  searchValue,
  onSearch,
  searchPlaceholder = 'Search…',
  filters = [], // [{ value, onChange, placeholder, options:[{value,label}] }]
  density,
  onDensityChange,
}) => (
  <div className="flex items-center gap-2.5 mb-4 flex-wrap">
    <div className="flex-1 min-w-[220px]">
      <Input
        allowClear
        size="middle"
        value={searchValue}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={searchPlaceholder}
        prefix={<Search size={15} style={{ opacity: 0.55 }} />}
      />
    </div>
    {filters.map((f, i) => (
      <Select
        key={i}
        value={f.value || undefined}
        onChange={f.onChange}
        placeholder={f.placeholder}
        options={f.options}
        allowClear
        style={{ minWidth: 140 }}
      />
    ))}
    {onDensityChange && (
      <Segmented
        className="ml-auto"
        value={density}
        onChange={onDensityChange}
        options={['Comfortable', 'Compact']}
      />
    )}
  </div>
);

/** Re-export AntD Button for consistent imports across admin screens. */
export { Button as AdminButton };
